import {
	ActionButtonContainer,
	ActionContainer,
} from '@dealstack/frontend/shared/components/Documents/styled';
import {
	DuplicateFile,
	ProcessingFile,
	UploadStatusStep,
} from '@dealstack/frontend/shared/components/Documents/types';
import { FileWithClassifier } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/types';
import { useFindRelatedFiles } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/hooks/useFindRelatedFiles';
import { useUploadDocumentsController } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/hooks/useUploadDocumentsController';
import { findDuplicateFiles } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/utils/findDuplicateFiles';
import { canCompleteFileAssociationStep } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/Footer/Footer.utils';
import { UploadStats } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/UploadStats';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FindRelatedFilesRes } from '@dealstack/shared/lib/generated';
import { DocumentLinkTarget } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { Button, Flex } from '@mantine/core';
import { DOCUMENT_LIBRARY_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

interface Props {
	setIsUploadActive: (isActive: boolean) => void;
	dealId: string;
	target?: DocumentLinkTarget;
	canButtonProceed: boolean;
	setCanButtonProceed: (canButtonProceed: boolean) => void;
	uploadFileData: FileWithClassifier[];
	setUploadFileData: (uploadFileData: FileWithClassifier[]) => void;
	uploadStatusStep: UploadStatusStep;
	setUploadStatusStep: (uploadStatusStep: UploadStatusStep) => void;
	setIsUploadingFiles: (isUploadingFiles: boolean) => void;
	localDuplicateFiles: {
		filesWithDuplicates: DuplicateFile[];
		totalDuplicates: number;
	};
	setLocalDuplicateFiles: (localDuplicateFiles: {
		filesWithDuplicates: DuplicateFile[];
		totalDuplicates: number;
	}) => void;
	processingFiles: ProcessingFile[];
	setProcessingFiles: (processingFiles: ProcessingFile[]) => void;
	relatedFilesInDeal: FindRelatedFilesRes[] | undefined;
	setRelatedFilesInDeal: (relatedFilesInDeal: FindRelatedFilesRes[]) => void;
	clearDocumentState: () => void;
	setNbrFilesUploaded: (nbrFilesUploaded: number) => void;
	setNbrFiles: (nbrFiles: number) => void;
}

export const Footer = ({
	setIsUploadActive,
	dealId,
	target,
	canButtonProceed,
	setCanButtonProceed,
	uploadFileData,
	uploadStatusStep,
	setUploadStatusStep,
	setIsUploadingFiles,
	localDuplicateFiles,
	setLocalDuplicateFiles,
	processingFiles,
	setProcessingFiles,
	relatedFilesInDeal,
	setRelatedFilesInDeal,
	clearDocumentState,
	setNbrFilesUploaded,
	setNbrFiles,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { uploadFiles } = useUploadDocumentsController({
		dealId,
		target,
		setIsUploadingFiles,
		processingFiles,
		setProcessingFiles,
		relatedFilesInDeal,
		setNbrFilesUploaded,
		setNbrFiles,
	});
	const { findRelatedFiles } = useFindRelatedFiles({
		dealId,
		target,
		setUploadStatusStep,
		localDuplicateFiles,
		setProcessingFiles,
		setRelatedFilesInDeal,
		uploadFiles,
	});

	const validateLocalFiles = async () => {
		const { filesWithDuplicates, totalDuplicates } = await findDuplicateFiles(
			uploadFileData,
			tCapManage('documents.errors.error-reading-file')
		);

		if (totalDuplicates > 0) {
			setCanButtonProceed(false);
			setLocalDuplicateFiles({
				filesWithDuplicates,
				totalDuplicates,
			});
			return;
		}

		// if no duplicates were found move on to the next step
		setUploadStatusStep(UploadStatusStep.fileDealDuplicate);
		await findRelatedFiles(filesWithDuplicates);
	};

	const isStepBlocked =
		uploadStatusStep === UploadStatusStep.fileDealDuplicate &&
		!canCompleteFileAssociationStep({
			relatedFiles: relatedFilesInDeal,
			processingFiles,
			hasUploadEntity: Boolean(target),
		});

	const handleContinueButtonPress = async () => {
		switch (uploadStatusStep) {
			case UploadStatusStep.addFiles:
				setUploadStatusStep(UploadStatusStep.validateUploads);
				await validateLocalFiles();
				break;
			case UploadStatusStep.validateUploads:
				setUploadStatusStep(UploadStatusStep.fileDealDuplicate);
				await findRelatedFiles(localDuplicateFiles.filesWithDuplicates);
				break;
			case UploadStatusStep.fileDealDuplicate:
				setUploadStatusStep(UploadStatusStep.uploadComplete);
				await uploadFiles({ processingFiles, relatedFiles: relatedFilesInDeal });
				break;
			case UploadStatusStep.uploadComplete:
				setIsUploadActive(false);
				break;
			default:
				break;
		}
	};

	return (
		<ActionContainer>
			<Flex align="flex-start">
				<UploadStats
					relatedFilesInDeal={relatedFilesInDeal}
					uploadFileData={uploadFileData}
					localDuplicateFiles={localDuplicateFiles}
				/>
			</Flex>
			<Flex align="flex-end">
				<ActionButtonContainer>
					<Button
						data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_CANCEL_BUTTON}
						variant="subtle"
						color="blue"
						onClick={() => {
							setIsUploadActive(false);
							clearDocumentState();
						}}
					>
						{tCapManage('document.upload_state.uploaded_section.action.cancel')}
					</Button>
					<Button
						data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_CONTINUE_BUTTON}
						variant="filled"
						color="blue"
						onClick={handleContinueButtonPress}
						disabled={!canButtonProceed || isStepBlocked}
					>
						{tCapManage('document.upload_state.uploaded_section.action.continue')}
					</Button>
				</ActionButtonContainer>
			</Flex>
		</ActionContainer>
	);
};
